/**
 * Markdown Preview View
 * Interactive markdown file preview
 */
import React, { useState } from 'react';
import { Box, Text, useInput } from 'ink';
import TextInput from 'ink-text-input';
import * as fs from 'fs';
import * as path from 'path';

export const MarkdownView: React.FC = () => {
    const [filePath, setFilePath] = useState('');
    const [content, setContent] = useState('');
    const [error, setError] = useState('');
    const [focusedField, setFocusedField] = useState<'file' | 'preview'>('file');

    useInput((input, key) => {
        if (key.tab) {
            setFocusedField(focusedField === 'file' ? 'preview' : 'file');
        } else if (key.return) {
            // Preview on Enter from any field
            handlePreview();
        }
    });

    const handlePreview = async () => {
        if (!filePath) {
            setError('Please enter a file path');
            return;
        }

        try {
            setError('');

            const resolvedPath = path.resolve(filePath);
            if (!fs.existsSync(resolvedPath)) {
                setError(`File not found: ${resolvedPath}`);
                return;
            }

            let fileContent = fs.readFileSync(resolvedPath, 'utf-8');

            // Pre-process Mermaid diagrams
            fileContent = fileContent.replace(/```mermaid\n([\s\S]*?)```/g, (match, code) => {
                return `\n🎨 **Mermaid Diagram**\n\n\`\`\`\n${code.trim()}\n\`\`\`\n\n💡 *Tip: Mermaid diagrams cannot be rendered in terminal. View in a Markdown viewer.*\n`;
            });

            // Import marked and marked-terminal dynamically
            const { marked } = await import('marked');
            const { default: markedTerminal } = await import('marked-terminal');

            // Configure marked with terminal renderer
            marked.use(markedTerminal() as any);

            // Render markdown
            const rendered = marked(fileContent) as string;
            setContent(rendered);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to preview file');
            setContent('');
        }
    };

    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    📄 Markdown Preview
                </Text>
            </Box>

            <Box
                flexDirection="column"
                borderStyle="single"
                borderColor="gray"
                paddingX={2}
                paddingY={1}
                marginBottom={1}
            >
                <Text bold color="yellow" marginBottom={1}>
                    Options:
                </Text>

                <Box marginBottom={1} flexDirection="column">
                    <Box marginBottom={0}>
                        <Text color={focusedField === 'file' ? 'green' : undefined}>
                            {focusedField === 'file' ? '▶ ' : '  '}File path:
                        </Text>
                    </Box>
                    <Box marginLeft={2} width={60}>
                        {focusedField === 'file' ? (
                            <TextInput
                                value={filePath}
                                onChange={setFilePath}
                                placeholder="./README.md or /path/to/file.md"
                            />
                        ) : (
                            <Text dimColor={!filePath}>{filePath || '(empty)'}</Text>
                        )}
                    </Box>
                </Box>

                <Box marginTop={1}>
                    <Text
                        bold
                        backgroundColor={focusedField === 'preview' ? 'green' : undefined}
                        color={focusedField === 'preview' ? 'black' : 'green'}
                    >
                        {focusedField === 'preview' ? '▶ ' : '  '}
                        [ Preview File ]
                    </Text>
                </Box>
            </Box>

            {error && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="red"
                    paddingX={2}
                    paddingY={1}
                    marginBottom={1}
                >
                    <Text color="red">✗ Error: {error}</Text>
                </Box>
            )}

            {content && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="green"
                    paddingX={2}
                    paddingY={1}
                >
                    <Box marginBottom={1}>
                        <Text bold color="green">
                            ✓ Preview:
                        </Text>
                    </Box>

                    <Box flexDirection="column">
                        <Text>{content}</Text>
                    </Box>
                </Box>
            )}

            <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={2}>
                <Text dimColor>
                    Tab: Next | Enter: Preview | Esc: Back
                </Text>
            </Box>
        </Box>
    );
};
